import {
  ChartDataset,
  ChartStyle
} from "state/ui/editor/elements/ChartElement";
import {
  bandScale,
  datasetDomain,
  defaultPlotArea,
  formatTickLabel,
  generateTicks,
  linearScale,
  seriesColorAt
} from "../chartScales";
import CartesianAxes from "./CartesianAxes";

interface Props {
  dataset: ChartDataset;
  style: ChartStyle;
  width: number;
  height: number;
}

const LineChart = ({ dataset, style, width, height }: Props) => {
  const plot = defaultPlotArea(width, height, {
    showLegend: style.showLegend,
    showAxes: style.showAxes,
    fontSize: style.fontSize
  });

  const domain = datasetDomain(dataset);
  const y = linearScale(domain, [plot.top + plot.height, plot.top]);
  const x = bandScale(dataset.headers, [plot.left, plot.left + plot.width]);
  const ticks = generateTicks(domain[0], domain[1]);

  const xCenter = (index: number) => x.scaleAt(index) + x.bandwidth / 2;

  return (
    <g>
      <CartesianAxes
        dataset={dataset}
        style={style}
        plot={plot}
        x={x}
        y={y}
        ticks={ticks}
      />

      {dataset.rows.map((row, rowIndex) => {
        const color = seriesColorAt(style.seriesColors, rowIndex);
        const points = row.values
          .map((value, valueIndex) => {
            if (valueIndex >= dataset.headers.length) return null;
            return { x: xCenter(valueIndex), y: y.scale(value), value };
          })
          .filter(
            (
              p
            ): p is {
              x: number;
              y: number;
              value: number;
            } => Boolean(p)
          );

        const path = points
          .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
          .join(" ");

        return (
          <g key={`series-${rowIndex}`}>
            <path
              d={path}
              stroke={color}
              strokeWidth={2}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {points.map((p, i) => (
              <g key={`point-${rowIndex}-${i}`}>
                <circle cx={p.x} cy={p.y} r={3} fill={color} />
                {style.showDataLabels && (
                  <text
                    x={p.x}
                    y={p.y - 6}
                    fontSize={style.fontSize}
                    fill={style.color}
                    fontFamily={style.fontFamily}
                    textAnchor="middle"
                  >
                    {formatTickLabel(p.value)}
                  </text>
                )}
              </g>
            ))}
          </g>
        );
      })}
    </g>
  );
};

export default LineChart;
